import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

import type { AuthStatus, AuthenticatedSession, AuthenticatedUser } from '@/domain/auth'

/**
 * useSessionStore : pinia store qui porte l'état de session côté UI.
 *
 * État :
 *   - status : 'unknown' | 'unauthenticated' | 'authenticated'
 *   - user   : payload identité (ou null)
 *   - csrfToken : synchronizer-token à mettre dans X-CSRF-Token sur les
 *                 mutations (lecture seule via getters)
 *
 * Sécurité :
 *   - csrfToken est en mémoire UNIQUEMENT (jamais persisté). Disparait
 *     au reload — le bootstrap appelle /auth/me pour le récupérer.
 *   - Pas de localStorage / sessionStorage : un XSS qui leak le token
 *     casse le pattern synchronizer-token (cf. spec §4.2).
 *   - Le cookie de session lui-même est HttpOnly, géré par le backend.
 */
export const useSessionStore = defineStore('session', () => {
    const status = ref<AuthStatus>('unknown')
    const user = ref<AuthenticatedUser | null>(null)
    const csrfToken = ref<string>('')

    const isAuthenticated = computed(() => status.value === 'authenticated')
    const isResolved = computed(() => status.value !== 'unknown')

    /**
     * setAuthenticated : copie défensive (pas de mutation du payload
     * d'origine). Le store devient l'unique propriétaire des références.
     */
    function setAuthenticated(session: AuthenticatedSession): void {
        user.value = { ...session.user }
        csrfToken.value = session.csrfToken
        status.value = 'authenticated'
    }

    /**
     * setUnauthenticated : reset complet. Appelé après logout, 401 /me
     * au boot, ou révocation de session détectée par un appel API mutant.
     */
    function setUnauthenticated(): void {
        user.value = null
        csrfToken.value = ''
        status.value = 'unauthenticated'
    }

    /**
     * markUnknown : utilisé seulement par les tests pour rejouer le
     * cycle de boot. La prod n'a pas besoin de revenir à 'unknown' une
     * fois le statut résolu.
     */
    function markUnknown(): void {
        user.value = null
        csrfToken.value = ''
        status.value = 'unknown'
    }

    return {
        status,
        user,
        csrfToken,
        isAuthenticated,
        isResolved,
        setAuthenticated,
        setUnauthenticated,
        markUnknown,
    }
})

/**
 * Type helper export : utile pour typer les params des composables
 * qui reçoivent le store en injection (faciliter les tests sans Pinia).
 */
export type SessionStore = ReturnType<typeof useSessionStore>
